import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { motion, type Variants } from "framer-motion";
import {
  bookSchema,
  breadcrumbSchema,
  collectionPageSchema,
  faqSchema,
  personSchema,
} from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";
const bookCover = "/img/book-cover-front.webp";

const QUIET_STACK_SRC = "/landing/books-quiet-stack.webp";
const QUIET_STACK_ALT =
  "A small stack of hardcover books on a warm wooden desk with reading glasses and a brass fountain pen, in soft natural window light.";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function Books() {
  const SEO_TITLE =
    "Books by Shariq Refai, MD, MBA, Psychiatrist and Author";
  const SEO_DESCRIPTION =
    "Three upcoming books from board-certified psychiatrist Shariq Refai, MD, MBA, on anxiety, overthinking, panic, and the patterns that keep people mentally stuck.";

  const collection = collectionPageSchema({
    path: "/books",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Books", path: "/books" },
    ],
    items: [
      { name: "Your Mind Is Full of Sh*t", path: "/books/your-mind-is-full-of-shit" },
      { name: "Your Mind Is Full of Sh*t: The Workbook", path: "/books/your-mind-is-full-of-shit-workbook" },
      { name: "The Havoc in Your Head", path: "/books/the-havoc-in-your-head" },
    ],
    speakableSelectors: [".quick-answer"],
  });

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        ogTitle="Books by Shariq Refai, MD, MBA"
        twitterTitle="Books by Shariq Refai, MD, MBA"
        description={SEO_DESCRIPTION}
        url="/books"
        image="/og-books.jpg"
        keywords="Shariq Refai books, psychiatrist author, books on anxiety, books on overthinking, mental health books, shrinkMD Publishing"
        schema={[
          personSchema,
          collection,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Books", path: "/books" },
          ]),
          bookSchema({
            path: "/books/your-mind-is-full-of-shit",
            name: "Your Mind Is Full of Sh*t",
            description:
              "A psychiatrist's guide to understanding overthinking, anxiety, and the lies in your head.",
            publisher: "shrinkMD Publishing",
            genre: ["Psychology", "Self-Help", "Mental Health", "Anxiety"],
          }),
          bookSchema({
            path: "/books/your-mind-is-full-of-shit-workbook",
            name: "Your Mind Is Full of Sh*t: The Workbook",
            description:
              "A psychiatrist's companion guide for noticing patterns in how you think, feel, and act.",
            publisher: "shrinkMD Publishing",
            genre: ["Psychology", "Self-Help", "Mental Health"],
          }),
          bookSchema({
            path: "/books/the-havoc-in-your-head",
            name: "The Havoc in Your Head",
            description:
              "A Psychiatrist's Reset for Anxiety, Overthinking, Panic, and the Thoughts That Won't Let Go.",
            publisher: "shrinkMD Publishing",
            genre: ["Psychology", "Self-Help", "Mental Health", "Anxiety", "Panic Disorder"],
          }),
          faqSchema([
            {
              question: "When will Your Mind Is Full of Sh*t be released?",
              answer:
                "Your Mind Is Full of Sh*t is scheduled for release in 2026 through shrinkMD Publishing. Pre-order details will be announced on shariqrefai.com.",
            },
            {
              question: "Who wrote Your Mind Is Full of Sh*t?",
              answer:
                "Your Mind Is Full of Sh*t was written by Shariq Refai, MD, MBA, a board-certified psychiatrist with more than fifteen years of experience in psychiatry and mental health care. He's the founder of shrinkMD and shrinQ, and the creator of the Unstuck app.",
            },
            {
              question: "What is the book about?",
              answer:
                "The book is about the four lies most modern adults absorbed without choosing them: control, productivity, positivity, and happiness. It's a psychiatrist's framework for understanding overthinking, anxiety, and what actually helps.",
            },
            {
              question: "Is the workbook a separate book?",
              answer:
                "Yes. The workbook is a companion to the main book but stands on its own. You don't have to read the main book first. It's a structured set of observation tools designed to help you notice patterns in your own thinking, feeling, and acting.",
            },
            {
              question: "How is The Havoc in Your Head different from the first book?",
              answer:
                "The first book is the framework. The Havoc in Your Head is the in-the-moment response. It's built around a four-step reset for use when anxiety, panic, or overthinking is happening in real time, not when you're calmly reading.",
            },
            {
              question: "Can these books replace therapy or medication?",
              answer:
                "No. The books are educational. They're not therapy, not psychiatric treatment, and not a substitute for clinical care. They sit alongside professional support, not instead of it.",
            },
          ]),
        ]}
      />

      {/* H1 / Hero */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Books" },
            ]}
          />
          <motion.div
            className="grid md:grid-cols-12 gap-10 md:gap-14 items-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <div className="md:col-span-7 order-2 md:order-1">
              {/* Credibility eyebrow */}
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
                Books · Shariq Refai, MD, MBA, FAPA · Board-Certified Psychiatrist
              </p>
              {/* Outcome-led H1 stays because it already names the topic */}
              <h1 className="mt-4 text-3xl md:text-5xl font-serif text-primary leading-[1.15] tracking-tight">
                Books by Shariq Refai on Anxiety and Overthinking
              </h1>
              {/* Subhead */}
              <p className="mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed">
                Three books from shrinkMD Publishing on the patterns behind modern anxiety, overthinking, and panic. Written for readers who've tried the apps, the worksheets, and the bestsellers and still can't quiet the noise.
              </p>
              {/* Proof strip */}
              <ul aria-label="Publishing credentials" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0">
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Written by a board-certified psychiatrist</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>15+ years of clinical experience</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Published by shrinkMD Publishing LLC</li>
              </ul>
              <p className="mt-6 text-xs text-muted-foreground font-light">
                Authored and editorially reviewed by Shariq Refai, MD, MBA · last reviewed{" "}
                <time dateTime="2026-05-15">May 15, 2026</time>
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <figure className="relative m-0">
                <img
                  src={QUIET_STACK_SRC}
                  alt={QUIET_STACK_ALT}
                  width={1200}
                  height={900}
                  className="w-full h-auto object-cover aspect-[4/3] shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </motion.div>
        </div>
      </section>

      {/* At a Glance / Quick Answer */}
      <section className="at-a-glance py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              Quick Answer
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-6">
            The Books in Brief
          </h2>
          <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
            Shariq Refai's upcoming books explore anxiety, overthinking, panic,
            and the patterns that keep people mentally stuck, written from the
            perspective of a board-certified psychiatrist with fifteen years
            of clinical practice. The first book is{" "}
            <em>Your Mind Is Full of Sh*t</em>, with a companion workbook and
            a follow-up reset framework called{" "}
            <em>The Havoc in Your Head</em>.
          </p>
        </div>
      </section>

      {/* Why These Books Exist */}
      <section className="why-these-books py-16 md:py-20 px-4 md:px-8 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              The Reason
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-8">
            Why These Books Exist
          </h2>
          <div className="space-y-6 text-base md:text-lg text-secondary font-light leading-relaxed">
            <p>
              Most mental health books fall into one of two camps. The clinical
              ones are written for clinicians. The popular ones are written
              for the bestseller list. The first group is unreadable for the
              people who actually need help. The second group is full of
              language that falls apart the moment a real anxious brain tries
              to use it.
            </p>
            <p>
              Dr. Refai's books are written for the third group. The reader
              who's already tried the apps. Already read the books. Already
              done the worksheets. The reader who's tired of being told to
              "just breathe" and watching that advice fail in the exact
              moment it was supposed to help. Each book is a working
              framework, written by a psychiatrist, in plain language, for
              the modern brain that won't shut off.
            </p>
          </div>
        </div>
      </section>

      {/* Book 1 - Primary */}
      <section className="book book-primary py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-[2fr_3fr] gap-12 lg:gap-16 items-start">
          <Link
            href="/books/your-mind-is-full-of-shit"
            className="block aspect-[2/3] bg-muted relative overflow-hidden self-start group"
          >
            <img
              src={bookCover}
              alt="Cover of Your Mind Is Full of Sh*t by Shariq Refai, MD, MBA."
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" width={500} height={781} loading="lazy" decoding="async"/>
          </Link>
          <div className="flex flex-col gap-6">
            <span className="status text-xs font-bold tracking-[0.25em] uppercase text-accent">
              Upcoming Release
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight">
              Your Mind Is Full of Sh*t
            </h2>
            <p className="subtitle text-lg md:text-xl text-primary font-serif italic leading-snug">
              A psychiatrist's guide to understanding overthinking, anxiety,
              and the lies in your head.
            </p>

            <div>
              <h3 className="font-serif text-xl text-primary mt-4 mb-2">What it's about</h3>
              <p className="text-secondary font-light leading-relaxed">
                The book takes the four lies most modern adults absorbed
                without choosing them. Control. Productivity. Positivity.
                Happiness. Each one looks reasonable from a distance and runs
                lives into the ground up close. Each chapter names the lie,
                shows the wiring underneath it, and gives you small repeatable
                practices that meet the body where it actually is. There's no
                manifesting. No five-step morning routine. No promises that
                you'll be happy if you wake up at four. Just the patterns Dr.
                Refai has watched run people's lives for fifteen years,
                written down honestly.
              </p>
            </div>

            <div>
              <h3 className="font-serif text-xl text-primary mt-4 mb-2">Who it's for</h3>
              <p className="text-secondary font-light leading-relaxed">
                The reader who's high-functioning on paper and exhausted in
                their own head. The executive who can't slow down. The parent
                who can't sleep. The student who's done the reading and still
                can't quiet the noise. The person who's been told they're "an
                anxious person" and is starting to wonder if that's a
                permanent identity. (It isn't.)
              </p>
            </div>

            <div>
              <h3 className="font-serif text-xl text-primary mt-4 mb-2">What you'll learn</h3>
              <p className="text-secondary font-light leading-relaxed">
                How to tell a thought from thinking, why your nervous system
                reads the absence of work as danger, what soggy shoes have to
                do with chronic stress, why the high-performer pattern is a
                wiring problem and not a personality, what actually quiets a
                vigilant body, and how to live a life that fits you instead
                of one you keep auditioning for.
              </p>
            </div>

            <div className="pt-4">
              <Link
                href="/books/your-mind-is-full-of-shit"
                className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
              >
                View Book Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Book 2 - Workbook */}
      <section className="book py-20 md:py-24 px-4 md:px-8 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-[2fr_3fr] gap-12 lg:gap-16 items-start">
          <Link
            href="/books/your-mind-is-full-of-shit-workbook"
            className="block aspect-[2/3] bg-[#EFECE6] border border-border/50 relative flex flex-col items-center justify-center p-8 text-center transition-colors hover:bg-[#EAE6DF] self-start"
          >
            <span className="text-sm tracking-widest uppercase text-muted-foreground">
              Cover coming soon
            </span>
            <h3 className="font-serif text-xl text-primary/70 mt-4">
              The Workbook
            </h3>
          </Link>
          <div className="flex flex-col gap-6">
            <span className="status text-xs font-bold tracking-[0.25em] uppercase text-muted-foreground">
              Companion coming soon
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight">
              Your Mind Is Full of Sh*t: The Workbook
            </h2>
            <p className="subtitle text-lg md:text-xl text-primary font-serif italic leading-snug">
              A psychiatrist's companion guide for noticing patterns in how
              you think, feel, and act.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">What it's about</h3>
            <p className="text-secondary font-light leading-relaxed">
              The workbook isn't a journal with prompts you've seen before.
              It's a structured set of reflective exercises drawn from years
              of clinical observation, translated into something you can
              actually use on your own. Where in your body does your weather
              live? Which of the four lies has the loudest grip on you right
              now? What's in your invisible backpack that doesn't belong
              there? Each exercise is small. The cumulative effect is what
              changes the relationship.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">Who it's for</h3>
            <p className="text-secondary font-light leading-relaxed">
              Readers of the first book who want to go deeper, and anyone
              who learns better by writing than by reading alone. Use it
              alongside the main book or on its own. There's no order
              requirement.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">What you'll learn</h3>
            <p className="text-secondary font-light leading-relaxed">
              How to recognize your own patterns in real time, name the four
              lies as they arrive, and translate insight into small daily
              moves that change the relationship over weeks rather than
              minutes.
            </p>
            <div className="pt-4">
              <Link
                href="/contact"
                className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
              >
                Notify me when available
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Book 3 - Havoc */}
      <section className="book py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-[2fr_3fr] gap-12 lg:gap-16 items-start">
          <Link
            href="/books/the-havoc-in-your-head"
            className="block aspect-[2/3] bg-[#2C3742] relative flex flex-col items-center justify-center p-8 text-center transition-colors hover:bg-secondary self-start"
          >
            <span className="text-sm tracking-widest uppercase text-white/80">
              Cover coming soon
            </span>
            <h3 className="font-serif text-xl text-white mt-4">
              The Havoc in Your Head
            </h3>
          </Link>
          <div className="flex flex-col gap-6">
            <span className="status text-xs font-bold tracking-[0.25em] uppercase text-muted-foreground">
              In Development
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight">
              The Havoc in Your Head
            </h2>
            <p className="subtitle text-lg md:text-xl text-primary font-serif italic leading-snug">
              A Psychiatrist's Reset for Anxiety, Overthinking, Panic, and the
              Thoughts That Won't Let Go.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">What it's about</h3>
            <p className="text-secondary font-light leading-relaxed">
              The first book is the framework. <em>The Havoc in Your Head</em>{" "}
              is the in-the-moment response. What to do when your mind has
              already taken over. When anxiety is at a nine. When panic hits
              in a grocery store. When the loop has already started and you
              can't think your way out of it.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">Who it's for</h3>
            <p className="text-secondary font-light leading-relaxed">
              Anyone whose mind moves faster than their nervous system can
              keep up with. People who already know the concepts and need a
              tool that works when the body is activated and thinking is
              offline.
            </p>
            <h3 className="font-serif text-xl text-primary mt-2 mb-1">What you'll learn</h3>
            <p className="text-secondary font-light leading-relaxed">
              A four-step reset that runs in under sixty seconds, designed
              for the part of the day when you actually need help, not the
              part where you're calmly reading. Repeatable. Portable. Built
              for real life.
            </p>
            <div className="pt-4">
              <Link
                href="/contact"
                className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
              >
                Notify me when available
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About the Author mini */}
      <section className="about-the-author-mini py-16 md:py-20 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-serif text-white mb-6">
            About the Author
          </h2>
          <p className="text-lg text-white/85 font-light leading-relaxed">
            Shariq Refai, MD, MBA, is a board-certified psychiatrist with
            more than fifteen years of experience in psychiatry and mental health care, founder of{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "books_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white underline decoration-white/40 underline-offset-4 hover:decoration-white"
            >
              shrinkMD
            </a>
            , and creator of the{" "}
            <a
              href={withNetworkUtm("https://beunstuck.app/", "books_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white underline decoration-white/40 underline-offset-4 hover:decoration-white"
            >
              Unstuck
            </a>{" "}
            app. He's based in Jacksonville, Florida.{" "}
            <Link
              href="/about"
              className="text-white underline decoration-white/40 underline-offset-4 hover:decoration-white"
            >
              Read his full bio.
            </Link>
          </p>
        </div>
      </section>

      {/* About shrinkMD Publishing */}
      <section className="about-publishing py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            The Imprint
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            About shrinkMD Publishing
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            shrinkMD Publishing is the imprint behind Dr. Refai's books.
            The mission is to release educational frameworks and literary
            works that take psychological concepts seriously, written by
            clinicians, for the general public. Not pop psychology. Not
            wellness fluff. Real ideas, written in plain language, with the
            disclaimers and the science the topic actually deserves.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            shrinkMD Publishing operates as an independent imprint focused
            on the intersection of clinical practice and public literacy.
          </p>
        </div>
      </section>

      {/* Which Book to Start With */}
      <section className="which-book py-20 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Reader's Guide
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-8">
            Which Book to Start With
          </h2>
          <ul className="grid gap-6 list-none p-0">
            <li className="border-l-2 border-accent pl-6">
              <h3 className="font-serif text-xl text-primary mb-2">
                If you're new to the work
              </h3>
              <p className="text-secondary font-light leading-relaxed">
                Start with <em>Your Mind Is Full of Sh*t</em>. It lays out
                the framework everything else builds on.
              </p>
            </li>
            <li className="border-l-2 border-accent pl-6">
              <h3 className="font-serif text-xl text-primary mb-2">
                If you want to put the ideas into practice
              </h3>
              <p className="text-secondary font-light leading-relaxed">
                Add the companion workbook. It turns the concepts into
                short, repeatable daily exercises.
              </p>
            </li>
            <li className="border-l-2 border-accent pl-6">
              <h3 className="font-serif text-xl text-primary mb-2">
                If you need something for the worst moments
              </h3>
              <p className="text-secondary font-light leading-relaxed">
                Read <em>The Havoc in Your Head</em>. It's a four-step
                reset for the part of the day when you actually need help.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Companion Reading from Perspectives */}
      <section className="companion-reading py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            From the Journal
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Companion Reading
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-6">
            While the books are in production, these essays cover the same
            ideas in shorter form.
          </p>
          <ul className="grid gap-4 list-none p-0">
            <li>
              <Link
                href="/perspectives/why-your-mind-feels-loudest-when-trying-to-rest"
                className="font-serif text-lg text-primary hover:text-accent transition-colors underline decoration-accent/30 underline-offset-4"
              >
                Why Your Mind Feels Loudest When You're Trying to Rest
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/why-thinking-harder-usually-makes-anxiety-worse"
                className="font-serif text-lg text-primary hover:text-accent transition-colors underline decoration-accent/30 underline-offset-4"
              >
                Why Thinking Harder Usually Makes Anxiety Worse
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/difference-between-a-thought-and-thinking"
                className="font-serif text-lg text-primary hover:text-accent transition-colors underline decoration-accent/30 underline-offset-4"
              >
                The Difference Between a Thought and Thinking
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment"
                className="font-serif text-lg text-primary hover:text-accent transition-colors underline decoration-accent/30 underline-offset-4"
              >
                Why Popular Mental Health Commentary Falls Short in the
                Moment
              </Link>
            </li>
          </ul>
        </div>
      </section>

      {/* Stay Informed (Notifications) */}
      <section className="stay-informed py-20 md:py-24 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Stay Informed
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-white leading-tight mt-6 mb-6">
            Get Release Updates
          </h2>
          <p className="text-white/85 font-light leading-relaxed mb-8">
            Sign up for low-frequency notifications about book launches,
            pre-order windows, and major announcements. No spam, no ads,
            and you can unsubscribe at any time.
          </p>
          <Link
            href="/notifications"
            className="inline-flex h-12 items-center justify-center bg-white text-secondary px-8 text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Sign Up for Notifications
          </Link>
        </div>
      </section>

      {/* For Press and Reviewers */}
      <section className="for-press py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            For Press
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            For Press and Reviewers
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Advance copy and review requests, foreword and blurb requests,
            and excerpt or reprint inquiries should go through the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>{" "}
            with "Publishing" selected. For press features about the
            books, select "Media &amp; Press" instead.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            Press assets including high-resolution cover art and the
            author headshot are available on the{" "}
            <Link
              href="/media"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              media page
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Books FAQ */}
      <section className="books-faq py-20 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Common Questions
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-10">
            Books FAQ
          </h2>
          <dl className="grid gap-8">
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                When will the books be released?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                All three books are scheduled for release in 2026 through
                shrinkMD Publishing.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Will there be audiobooks?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Audiobook details will be announced alongside print and
                ebook release.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                How will the books be distributed?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Distribution will be announced as release approaches. Sign
                up for{" "}
                <Link
                  href="/notifications"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  notifications
                </Link>{" "}
                to be told first.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Are sample chapters available?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Sample chapters and excerpts will be made available closer
                to release. Press reviewers can request advance copy
                through the contact form.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Are the books therapy?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                No. The books are educational and informational. They
                aren't therapy, psychiatric treatment, diagnosis, or
                individualized medical advice. For clinical care, visit{" "}
                <a
                  href={withNetworkUtm("https://shrinkmd.com/", "books_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  shrinkmd.com
                </a>
                .
              </dd>
            </div>
          </dl>
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="books_next_step"
            destinations={["shrinkopedia", "shrinq", "shrinkmd"]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
